import React, { useMemo } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Paper,
  Stack,
  TextField, Tooltip,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import { Controller, useForm, useWatch } from "react-hook-form";

import { reportTypes } from "../../../constants/appConstants";
import {usePermissions} from "../../../hooks/usePermissions";
import {useAccessDeniedMessage} from "../../../hooks/useAccessDeniedMessage";
import {
  CREATE_DOCUMENT_REPORT,
  READ_SALE_INVOICE,
  READ_SALE_CREDIT_NOTE,
  READ_PURCHASE_CREDIT_NOTE,
  READ_PURCHASE_INVOICE,
  CREATE_SALE_INVOICE,
  CREATE_PURCHASE_CREDIT_NOTE,
  CREATE_PURCHASE_INVOICE, CREATE_SALE_CREDIT_NOTE
} from "../../../constants/eInvoicePermissions";
import PermissionDeniedWrapper from "../../../components/PermissionDeniedWrapper";
import {getDocumentReportGenerateAccess} from "../utils/getDocumentReportGenerateAccess";
import {useSelector} from "react-redux";
import TimePeriodField, {
  formatRangeLabel,
  inclusiveDayCount,
} from "../../../components/form/TimePeriodField";
import VatProfileSelect, {
    getProfileVat, keepRegisteredProfiles,
} from "../../../components/form/VatProfileSelect";
import {formatAccessDeniedMessage} from "../../../utils/accessDeniedMessage";

const DOCUMENT_TYPE_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "SALES_INVOICE", label: "Sales Invoice" },
  { value: "SALES_CREDIT_NOTE", label: "Sales Credit Note" },
  { value: "PURCHASE_INVOICE", label: "Purchase Invoice" },
  { value: "PURCHASE_CREDIT_NOTE", label: "Purchase Credit Note" },
];

const STATUS_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "PENDING", label: "Pending" },
  { value: "VALIDATION_FAILED", label: "Validation Failed" },
  { value: "REJECTED", label: "Rejected" },
  { value: "APPROVED", label: "Approved" },
];

const REPORT_FORMAT_OPTIONS = [
  { value: "SUMMARY", label: "Summary Report" },
  { value: "DETAILED", label: "Detailed Report" },
];

const SINGLE_LINE_SELECT_SX = {
  "& .MuiOutlinedInput-root": { flexWrap: "nowrap", overflow: "hidden" },
  "& .MuiAutocomplete-input": { minWidth: 30 },
  "& .MuiChip-root": { maxWidth: 140 },
};

const MAX_RANGE_DAYS = 365;

const getDefaultFormValues = () => ({
  vatProfiles: [],
  documentTypes: [DOCUMENT_TYPE_OPTIONS[0]],
  statuses: [STATUS_OPTIONS[0]],
  reportFormat: "SUMMARY",
  startDate: dayjs().startOf("month"),
  endDate: dayjs(),
});

const renderCollapsedTags = (getLabel, getKey) => (value, getTagProps) => {
  const tags = value
    .slice(0, 1)
    .map((option, index) => (
      <Chip
        {...getTagProps({ index })}
        key={getKey(option, index)}
        label={getLabel(option)}
        size="small"
      />
    ));

  if (value.length > 1) {
    tags.push(
      <Typography
        key="more"
        variant="body2"
        sx={{ ml: 0.5, whiteSpace: "nowrap", color: "text.secondary" }}
      >
        +{value.length - 1}
      </Typography>,
    );
  }

  return tags;
};

const handleAllAwareMultiChange = (value, allOption, onChange) => {
  if (!value || value.length === 0) {
    onChange([allOption]);
    return;
  }
  const last = value[value.length - 1];
  if (last.value === "ALL") {
    onChange([allOption]);
    return;
  }
  onChange(value.filter((option) => option.value !== "ALL"));
};

const ACCESS_DENIED_MSG = "You don't have permission to perform this action. Please contact your organization admin ({email})."

const DocumentReportFilters = ({ loading, onGenerate }) => {

    const {hasEInvoicePermission} = usePermissions();
    const accessDeniedMessage = useAccessDeniedMessage();
    const { org } = useSelector((/** @type {any} */ state) => state.org);
    const rootUserEmail = org?.root_user_email ?? '';
    const canCreate = hasEInvoicePermission(CREATE_DOCUMENT_REPORT);
    const { hasSalesPurchaseScope, tooltipMessage } = getDocumentReportGenerateAccess({
        hasEInvoicePermission,
        adminEmail: rootUserEmail,
    });
    const generateTooltip = !canCreate ? accessDeniedMessage : tooltipMessage;
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        setError,
        clearErrors,
        formState: {errors},
    } = useForm({
        defaultValues: getDefaultFormValues(),
    });
  const startDate = useWatch({ control, name: "startDate" });
  const endDate = useWatch({ control, name: "endDate" });
  const vatProfiles = useWatch({ control, name: "vatProfiles" });
  const documentTypes = useWatch({ control, name: "documentTypes" });
  const statuses = useWatch({ control, name: "statuses" });
  const reportFormat = useWatch({ control, name: "reportFormat" });

  const dayCount = inclusiveDayCount(startDate, endDate);
  const isDateRangeValid = dayCount !== null && dayCount <= MAX_RANGE_DAYS;
  const registeredVatProfiles = keepRegisteredProfiles(vatProfiles);
  const canGenerate =
    registeredVatProfiles.length > 0 &&
    !!startDate &&
    !!endDate &&
    isDateRangeValid;

  const hasReadOrWriteAccess = (option) =>{
    if(option.value === "SALES_INVOICE"){
      return hasEInvoicePermission(CREATE_SALE_INVOICE) || hasEInvoicePermission(READ_SALE_INVOICE);
    }else if(option.value === "SALES_CREDIT_NOTE"){
      return hasEInvoicePermission(CREATE_SALE_CREDIT_NOTE) || hasEInvoicePermission(READ_SALE_CREDIT_NOTE);
    }else if(option.value === "PURCHASE_INVOICE"){
      return hasEInvoicePermission(CREATE_PURCHASE_INVOICE) || hasEInvoicePermission(READ_PURCHASE_INVOICE);
    }else if(option.value === "PURCHASE_CREDIT_NOTE"){
      return hasEInvoicePermission(CREATE_PURCHASE_CREDIT_NOTE) || hasEInvoicePermission(READ_PURCHASE_CREDIT_NOTE);
    }
    return true;
  }

  const getAccessDeniedReason = (option) =>
      hasReadOrWriteAccess(option) ? "" : formatAccessDeniedMessage(ACCESS_DENIED_MSG, rootUserEmail);

  const handleDateRangeChange = (start, end) => {
    setValue("startDate", start, { shouldDirty: true, shouldValidate: true });
    setValue("endDate", end, { shouldDirty: true, shouldValidate: true });
    clearErrors(["startDate", "endDate"]);
  };

  const normalizeMultiSelect = (selected, allValue = "ALL") => {
    if (!selected || selected.length === 0) {
      return [allValue];
    }
    const values = selected.map((option) => option.value ?? option);
    if (values.includes(allValue)) {
      return [allValue];
    }
    return values;
  };

  const appliedFilterChips = useMemo(() => {
    const chips = [];

    (vatProfiles || []).forEach((profile) => {
      const vat = getProfileVat(profile);
      const name = profile?.name || "";
      chips.push({
        key: `vat-${profile?.profile_id || profile?.id || vat}`,
        label: name ? `VAT: ${vat} — ${name}` : `VAT: ${vat}`,
        onDelete: () => {
          setValue(
            "vatProfiles",
            (vatProfiles || []).filter(
              (item) =>
                (item?.profile_id || item?.id) !==
                (profile?.profile_id || profile?.id),
            ),
          );
        },
      });
    });

    (documentTypes || [])
      .filter((option) => option.value !== "ALL")
      .forEach((option) => {
        chips.push({
          key: `doc-type-${option.value}`,
          label: `Document Type: ${option.label}`,
          onDelete: () => {
            const next = (documentTypes || []).filter(
              (item) => item.value !== option.value,
            );
            setValue(
              "documentTypes",
              next.length ? next : [DOCUMENT_TYPE_OPTIONS[0]],
            );
          },
        });
      });

    (statuses || [])
      .filter((option) => option.value !== "ALL")
      .forEach((option) => {
        chips.push({
          key: `status-${option.value}`,
          label: `Status: ${option.label}`,
          onDelete: () => {
            const next = (statuses || []).filter(
              (item) => item.value !== option.value,
            );
            setValue("statuses", next.length ? next : [STATUS_OPTIONS[0]]);
          },
        });
      });

    if (reportFormat && reportFormat !== "SUMMARY") {
      chips.push({
        key: "report-format",
        label: "Report Type: Detailed Report",
        onDelete: () => setValue("reportFormat", "SUMMARY"),
      });
    }

    if (startDate && endDate) {
      chips.push({
        key: "time-period",
        label: `Time Period: ${formatRangeLabel(startDate, endDate)}`,
        onDelete: () => {
          setValue("startDate", dayjs().startOf("month"));
          setValue("endDate", dayjs());
        },
      });
    }

    return chips;
  }, [
    vatProfiles,
    documentTypes,
    statuses,
    reportFormat,
    startDate,
    endDate,
    setValue,
  ]);

  const handleClearAll = () => {
    reset(getDefaultFormValues());
  };

  const handleGenerateReport = (data) => {
    const registeredProfiles = keepRegisteredProfiles(data.vatProfiles);
    if (registeredProfiles.length === 0) {
      setError("vatProfiles", {
        type: "required",
        message: "VAT Selection is required",
      });
      return;
    }
    if (registeredProfiles.length !== (data.vatProfiles || []).length) {
      setValue("vatProfiles", registeredProfiles);
    }
    if (!data.startDate) {
      setError("startDate", {
        type: "required",
        message: "Start date is required",
      });
    }
    if (!data.endDate) {
      setError("endDate", {
        type: "required",
        message: "End date is required",
      });
    }
    if (!data.startDate || !data.endDate) {
      return;
    }
    const selectedDays = inclusiveDayCount(data.startDate, data.endDate);
    if (selectedDays === null || selectedDays > MAX_RANGE_DAYS) {
      setError("endDate", {
        type: "validate",
        message: "Time period must not exceed 365 days",
      });
      return;
    }
    const reportData = {
      created_after: data.startDate.format("YYYY-MM-DD"),
      created_before: data.endDate.format("YYYY-MM-DD"),
      vat_numbers: registeredProfiles.map((profile) => getProfileVat(profile)),
      document_types: normalizeMultiSelect(data.documentTypes),
      statuses: normalizeMultiSelect(data.statuses),
      report_format: data.reportFormat || "SUMMARY",
      type: reportTypes.DOCUMENT_REPORT,
    };
    onGenerate(reportData);
    reset(getDefaultFormValues());
  };

  const dateFieldError = errors.startDate?.message || errors.endDate?.message;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Stack spacing={2.5}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          <VatProfileSelect
            multiple
            required
            name="vatProfiles"
            label="VAT Number"
            control={control}
            errors={errors.vatProfiles}
          />

          <Controller
            name="documentTypes"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                sx={SINGLE_LINE_SELECT_SX}
                options={DOCUMENT_TYPE_OPTIONS}
                getOptionLabel={(option) => option.label}
                getOptionDisabled={(option) => Boolean(getAccessDeniedReason(option))}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                value={field.value || []}
                onChange={(_, value) =>
                  handleAllAwareMultiChange(
                    value,
                    DOCUMENT_TYPE_OPTIONS[0],
                    field.onChange,
                  )
                }
                renderOption={(props, option) => {
                  const { key, onClick, onMouseDown, ...optionProps } = props;
                  const disabledReason = getAccessDeniedReason(option);
                  const blockDisabledSelection = (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  };

                  return (
                      <Box
                          key={key}
                          component="li"
                          {...optionProps}
                          onMouseDown={
                            disabledReason ? blockDisabledSelection : onMouseDown
                          }
                          onClick={disabledReason ? blockDisabledSelection : onClick}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            opacity: disabledReason ? 0.5 : 1,
                            cursor: disabledReason ? "not-allowed" : "pointer",
                          }}
                      >
                        {disabledReason ? (
                            <Tooltip title={disabledReason} placement="right">
                              <Box
                                  component="span"
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    maxWidth: "100%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    pointerEvents: "auto",
                                    fontSize: 16,
                                  }}
                              >{option?.label}</Box>
                            </Tooltip>
                        ) : (
                            <Box
                                component="span"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  maxWidth: "100%",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  pointerEvents: "auto",
                                  fontSize: 16,
                                }}
                            >{option?.label}</Box>
                        )}
                      </Box>
                  );
                }}
                renderTags={renderCollapsedTags(
                  (option) => option.label,
                  (option) => option.value,
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Document Type" />
                )}
              />
            )}
          />

          <Controller
            name="statuses"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                size="small"
                disableCloseOnSelect
                sx={SINGLE_LINE_SELECT_SX}
                options={STATUS_OPTIONS}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                value={field.value || []}
                onChange={(_, value) =>
                  handleAllAwareMultiChange(
                    value,
                    STATUS_OPTIONS[0],
                    field.onChange,
                  )
                }
                renderTags={renderCollapsedTags(
                  (option) => option.label,
                  (option) => option.value,
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Status" />
                )}
              />
            )}
          />

          <Controller
            name="reportFormat"
            control={control}
            render={({ field }) => (
              <Autocomplete
                size="small"
                options={REPORT_FORMAT_OPTIONS}
                disableClearable
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                value={
                  REPORT_FORMAT_OPTIONS.find(
                    (option) => option.value === (field.value || "SUMMARY"),
                  ) || REPORT_FORMAT_OPTIONS[0]
                }
                onChange={(_, value) =>
                  field.onChange(value?.value || "SUMMARY")
                }
                renderInput={(params) => (
                  <TextField {...params} label="Report Type" />
                )}
              />
            )}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(4, minmax(0, 1fr))",
            },
            gap: 2,
            alignItems: "start",
          }}
        >
          <TimePeriodField
            startDate={startDate}
            endDate={endDate}
            maxDays={MAX_RANGE_DAYS}
            required
            error={!!dateFieldError}
            helperText={dateFieldError}
            onChange={handleDateRangeChange}
          />

                    <Box
                        sx={{
                            gridColumn: {xs: "auto", md: "2 / -1"},
                            display: "flex",
                            justifyContent: {xs: "stretch", md: "flex-end"},
                            alignItems: "flex-start",
                        }}
                    >
                        <PermissionDeniedWrapper show={!hasSalesPurchaseScope || !canCreate} title={generateTooltip}>
                            <Button
                                id="generate-report"
                                variant="contained"
                                startIcon={<DescriptionOutlinedIcon/>}
                                disabled={!canGenerate || loading || !hasSalesPurchaseScope || !canCreate}
                                onClick={handleSubmit(handleGenerateReport)}
                                sx={{
                                    minWidth: 180,
                                    height: 40,
                                    width: {xs: "100%", md: "auto"},
                                }}
                            >
                                {loading ? "Generating..." : "Generate Report"}
                            </Button>
                        </PermissionDeniedWrapper>
                    </Box>
                </Box>

        <Divider />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Applied filters
            </Typography>
            {appliedFilterChips.length === 0 ? (
              <Typography variant="body2" color="text.disabled">
                None
              </Typography>
            ) : (
              appliedFilterChips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  onDelete={chip.onDelete}
                  size="small"
                />
              ))
            )}
          </Stack>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={handleClearAll}
            sx={{ whiteSpace: "nowrap" }}
          >
            Clear all
          </Link>
        </Stack>

        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          sx={{
            bgcolor: "grey.50",
            color: "text.secondary",
            border: "1px solid",
            borderColor: "divider",
            "& .MuiAlert-icon": { color: "text.secondary" },
          }}
        >
          Reports are generated asynchronously. A secure download link is
          emailed to your registered address and expires after 30 days.
        </Alert>
      </Stack>
    </Paper>
  );
};

export default DocumentReportFilters;
